import { FaLightbulb, FaTimes, FaPlay, FaCheck, FaBook, FaFilm, FaBolt, FaTrophy, FaQuoteLeft, FaListOl, FaBalanceScale, FaCheckCircle, FaTimesCircle, FaStar, FaArrowRight, FaGraduationCap } from 'react-icons/fa';
import { HiLightBulb, HiSparkles } from 'react-icons/hi';
import { BsLightningChargeFill, BsBookmarkStarFill } from 'react-icons/bs';
import { IoMdRocket } from 'react-icons/io';
import { RiFileList3Fill } from 'react-icons/ri';

// Detailed content for each principle
const principleDetails = {
  1: {
    subtitle: "Control your supply to ensure timely payments",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "When doing B2B business, there are 2 modules in payment recovery:\n1. Getting stuck payments back\n2. Ensuring future payments don't get stuck\n\nThe second is MORE important! This principle teaches you how to prevent payment delays by controlling product supply."
      },
      {
        title: "🎬 Real Scenario",
        type: "scenario",
        content: "1st January: You delivered goods worth ₹10 lakhs with 30 days payment terms.\n31st January: You raised the bill and asked for payment.\n1st February: Customer says 'Please wait for a few more days' OR 'Take partial payment and send more products of ₹5 lakhs'"
      },
      {
        title: "⚡ THE GOLDEN RULE",
        type: "highlight",
        content: "IF THE PAYMENT DOESN'T COME AS PER THE COMMITMENT, THEN MORE PRODUCTS WILL NOT GO!"
      },
      {
        title: "🏆 Coach's Advice",
        type: "tip",
        content: "When you keep sending more products without receiving payment for previous supplies, you're essentially giving the customer FREE CREDIT and losing your leverage.\n\nRemember: Your products are your power. Once delivered, you lose control!"
      },
      {
        title: "✅ What To Do Instead",
        type: "steps",
        items: [
          "Stop Supply: Don't send additional products until you receive payment for the current outstanding amount.",
          "Stand Firm: Don't get emotional. This is business, not charity.",
          "Communicate Clearly: 'Sir, as per our agreement, payment was due on [date]. Once we receive this, we'll be happy to process your next order.'",
          "No Exceptions: Even if the customer threatens to go to competitors, don't give in."
        ]
      }
    ],
    quote: "The moment you lose control of your supply, you lose control of your payments. Keep your leverage intact!"
  },
  2: {
    subtitle: "Don't increase credit period without payment",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "If you gave 30 days credit and payment didn't come, NEVER extend it to 45 or 60 days! This is a slippery slope that leads to permanent non-payment."
      },
      {
        title: "🎬 The Typical Situation",
        type: "scenario",
        content: "Day 30: Payment due date arrives.\nCustomer says: 'Sir, I'm facing some cash flow issues. Please give me 15 more days.'\nYour response should be: 'I understand, but our agreement was 30 days. Let's find another solution - partial payment, post-dated cheque, or collateral.'"
      },
      {
        title: "⚠️ WARNING",
        type: "highlight",
        content: "When you extend credit terms once, the customer will ask again and again. 30 days becomes 60, then 90, then never!"
      },
      {
        title: "🏆 Why This Happens",
        type: "tip",
        content: "When customers realize you're flexible with deadlines, they prioritize paying other strict vendors first and delay payment to you indefinitely.\n\nYou become the 'adjustable' vendor - the last one to get paid!"
      }
    ],
    dos: ["Stick to original terms firmly", "Offer alternative solutions", "Take post-dated cheques", "Accept partial payments", "Get additional security"],
    donts: ["Extend credit period easily", "Accept excuses without action", "Give unlimited extensions", "Be too 'understanding'", "Set new payment dates verbally"],
    quote: "A flexible payment policy makes you the last person to get paid. Be firm, be clear, be consistent!"
  },
  3: {
    subtitle: "Partial payment before more supply",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "If a customer owes you money and wants more products, insist on partial payment of the outstanding amount before dispatching new goods."
      },
      {
        title: "🎬 Typical Scenario",
        type: "scenario",
        content: "Outstanding: Customer owes ₹10 lakhs\nNew Order: Customer wants ₹5 lakhs worth of new products\nCustomer says: 'Send the new order, I'll pay both amounts together next month.'\nYour response: 'Please pay ₹5 lakhs from the outstanding ₹10 lakhs first, then we'll dispatch your new order of ₹5 lakhs.'"
      },
      {
        title: "🏆 The Strategy Behind This",
        type: "tip",
        content: "Why this works:\n1. Reduces your exposure (outstanding reduces from ₹10L to ₹5L)\n2. Shows customer you're serious about payments\n3. Customer proves they have money before getting more goods\n4. Maintains your leverage"
      },
      {
        title: "💡 The Formula",
        type: "highlight",
        content: "New Order Value = Minimum Part Payment Required\nIf customer wants ₹X worth of new products, they must pay ₹X from old dues first!"
      },
      {
        title: "📖 Real Example",
        type: "story",
        content: "A textile wholesaler had a customer owing ₹15 lakhs. Customer placed new order for ₹8 lakhs.\n\nOld approach: Send goods, hope to collect ₹23 lakhs later (this never happens!)\n\nNew approach: 'Pay ₹8 lakhs from ₹15 lakhs dues, then get new ₹8 lakhs stock.' Customer paid! Outstanding reduced to ₹7 lakhs.\n\nResult: Instead of ₹23 lakhs stuck, only ₹7 lakhs remained."
      }
    ],
    quote: "Never increase your risk. If they want more products, they must show they can pay by clearing part of old dues first!"
  },
  4: {
    subtitle: "Stick to your credit policy strictly",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "If your company policy is 30 days credit, never give 45 or 60 days to anyone - no matter how big the customer is or how large the order!"
      },
      {
        title: "🎬 The Temptation",
        type: "scenario",
        content: "Big customer says: 'I'll give you a ₹50 lakh order, but I need 60 days credit instead of 30.'\nYour mind thinks: 'Wow, ₹50 lakhs! Let me make an exception...'\nReality 60 days later: Customer doesn't pay. Now you're stuck with ₹50 lakhs for months!"
      },
      {
        title: "⚠️ THE TRUTH",
        type: "highlight",
        content: "If someone asks for 60 days credit when your policy is 30 days, it means:\n✗ They don't have good cash flow\n✗ They're used to delaying payments\n✗ They'll likely delay even beyond 60 days\n✗ They're testing your boundaries"
      },
      {
        title: "🏆 Coach's Wisdom",
        type: "tip",
        content: "A customer who respects your business will respect your credit policy.\n\nIf they can't work within your 30-day terms, they're not the right customer for you - regardless of order size!"
      },
      {
        title: "📖 Coach's Personal Experience",
        type: "story",
        content: "I once gave 90 days credit to a 'big' customer who promised a ₹1 crore annual business. First order ₹25 lakhs went out.\n\n90 days passed. No payment. 120 days. 150 days. Finally recovered after 8 months with 40% loss.\n\nMeanwhile, I had to take loans to run my business. Never again!"
      }
    ],
    dos: ["'Our policy is 30 days for everyone'", "'We can offer advance payment discount instead'", "'We maintain same policy for fairness'", "'Let's work within 30 days creatively'"],
    donts: ["'Ok, since order is big, take 60 days'", "'For you, I'll make exception'", "'This one time only, 90 days ok'", "'Let me check with management'"],
    quote: "A big order with extended credit is not an opportunity - it's a trap! Stick to your policy no matter what!"
  },
  5: {
    subtitle: "Connect beyond business transactions",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "The customer who considers you a friend will pay you on time. The one who sees you as just a vendor will make you wait.\n\nBuild genuine personal relationships beyond business transactions."
      },
      {
        title: "🏆 Why Personal Relationships Matter",
        type: "tip",
        content: "When payment time comes, customers pay in this order:\n1. Vendors who call regularly and have personal relationship\n2. Vendors who have legal documentation\n3. Aggressive vendors who threaten\n4. Passive vendors who just wait (they wait forever!)"
      },
      {
        title: "🎬 The Difference",
        type: "scenario",
        content: "Vendor A: Only calls for orders and payments. Formal communication only.\n\nVendor B: Calls to wish on festivals, asks about family, remembers birthdays, occasionally sends small gifts.\n\nWhen cash crunch comes: Customer pays Vendor B first. Why? Because there's emotional connection!"
      },
      {
        title: "📱 How To Build Personal Relationships",
        type: "steps",
        items: [
          "Regular Calls: Call once a week, not just for business. Ask 'How's everything going?'",
          "Remember Important Dates: Birthday, anniversary, festivals - send wishes via WhatsApp",
          "Know Their Business: Understand their challenges, offer suggestions",
          "Meet In Person: Visit their office/shop quarterly, not just when payment is due",
          "Small Gestures: Festival gifts, business anniversary wishes, congratulations on achievements",
          "Be Genuinely Helpful: Share industry insights, introduce useful contacts"
        ]
      },
      {
        title: "📖 Real Example",
        type: "story",
        content: "A supplier of packaging material had 50 customers. He started calling top 20 customers weekly - not for business, just friendly conversation.\n\nHe remembered their children's names, asked about their business, wished on festivals.\n\nResult after 6 months: These 20 customers started paying 5-7 days before due date without reminder!\n\nOther 30 customers still delayed payments regularly."
      }
    ],
    dos: ["Call regularly (weekly/bi-weekly)", "Remember personal details", "Wish on festivals and occasions", "Be genuinely interested", "Help beyond your products", "Visit in person periodically"],
    donts: ["Only call for orders/payments", "Be too formal always", "Fake friendliness", "Expect immediate returns", "Be intrusive or annoying", "Only contact when you need something"],
    quote: "People do business with people they like. They pay on time to people they respect and care about!"
  },
  6: {
    subtitle: "Get money before delivering goods",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "The BEST way to ensure payment is to get it BEFORE delivery! Even if you can't get full payment, always take some advance.\n\nAdvance payment is not just money - it's commitment from the customer."
      },
      {
        title: "💡 THE GOLDEN RULE",
        type: "highlight",
        content: "NO ADVANCE = NO COMMITMENT = HIGH RISK!"
      },
      {
        title: "🏆 Why Advance Payment Works",
        type: "tip",
        content: "1. Reduces Risk: Your money is already secured before dispatch\n2. Shows Seriousness: Customer who pays advance is serious buyer\n3. Improves Cash Flow: You get money to fulfill the order\n4. Filters Bad Customers: Time-wasters won't pay advance\n5. Reduces Cancellations: Customer won't back out after paying"
      },
      {
        title: "🎬 Three Scenarios",
        type: "scenario",
        content: "Scenario 1 - Full Advance:\nCustomer pays 100% before delivery. Zero risk! Best option always.\n\nScenario 2 - Partial Advance:\nCustomer pays 30-50% advance, balance on delivery or credit. Low risk! Good option.\n\nScenario 3 - No Advance:\nCustomer wants full credit, zero advance. High risk! Avoid if possible."
      },
      {
        title: "💰 Advance Payment Models",
        type: "steps",
        items: [
          "100% Advance: Best for new customers, high-value orders, customized products",
          "50% Advance + 50% on Delivery: Good balance for regular customers",
          "30% Advance + 70% Credit: For trusted customers with good history",
          "Token Advance + Credit: Minimum ₹10,000 or 10% - at least shows commitment"
        ]
      },
      {
        title: "📖 Real Success Story",
        type: "story",
        content: "A furniture manufacturer used to give full credit. Payment collection was nightmare - average 90 days, many bad debts.\n\nChanged policy: 50% advance mandatory for all orders.\n\nCustomer reaction: 'Nobody asks for advance!' Many customers left.\n\nResult after 6 months:\n• Sales reduced by 30% but PROFITS increased by 50%!\n• Zero bad debts\n• Cash flow became positive\n• Could negotiate better terms with suppliers\n• Remaining customers were all quality customers"
      }
    ],
    dos: ["'Our policy is 50% advance for all orders'", "'Advance secures your order priority'", "'We start production after advance'", "'This protects both of us'"],
    donts: ["'If possible, can you give advance?'", "'We usually don't ask but...'", "'It's okay if you can't give advance'", "'Other customers give, so you also...'"],
    quote: "Advance payment is the best payment! Get money before delivery, sleep peacefully after delivery!"
  },
  7: {
    subtitle: "Secure payment commitment in advance",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Always take Post Dated Cheques (PDC) at the time of delivery itself. This gives you legal protection and ensures customer commitment.\n\nPDC is your insurance policy for payment recovery!"
      },
      {
        title: "🎬 How It Works",
        type: "scenario",
        content: "At Delivery Time:\n'Sir, your payment is due on 30th March. Please give me a cheque dated 30th March today itself.'\n\nCustomer says: 'I'll pay by bank transfer on due date.'\n\nYour response: 'That's fine sir, this cheque is just for our records. If you transfer before 30th, I'll return your cheque immediately.'"
      },
      {
        title: "🏆 Why PDC is Powerful",
        type: "tip",
        content: "1. Legal Document: Cheque is legally enforceable. If it bounces, criminal case under Section 138!\n2. Shows Commitment: Customer who gives PDC is serious about payment\n3. Easy to Deposit: On due date, just deposit cheque - no need to ask!\n4. Prevents Excuses: Customer can't say 'I forgot' or 'I'm busy'\n5. Speeds Recovery: If cheque bounces, you can take legal action immediately"
      },
      {
        title: "⚠️ IMPORTANT RULES FOR PDC",
        type: "highlight",
        content: "✓ Take PDC for FULL AMOUNT (not partial)\n✓ Check all details: Name, Amount, Date, Signature\n✓ Take from COMPANY ACCOUNT (not personal)\n✓ Get it SIGNED in front of you\n✓ Click PHOTO of cheque immediately\n✓ Store safely - it's like CASH!"
      },
      {
        title: "📝 PDC Collection Strategy",
        type: "steps",
        items: [
          "At Order Time: 'Sir, we'll need post-dated cheque at delivery'",
          "At Delivery Time: Collect PDC before unloading goods",
          "Verify Immediately: Check all details on the spot",
          "Give Receipt: 'Received cheque no. XXX dated 30-03-2025 for ₹5,00,000'",
          "Before Due Date: Remind customer 3 days before",
          "On Due Date: Deposit cheque or get payment"
        ]
      },
      {
        title: "📖 The PDC Success Story",
        type: "story",
        content: "A wholesaler was struggling with delayed payments. Average collection time: 75 days.\n\nNew Rule: NO delivery without post-dated cheque!\n\nWhat happened:\n• Some customers resisted initially\n• Stood firm: 'This is our policy for everyone'\n• Customers realized he's serious\n\nResults after 3 months:\n• Average collection time: 32 days (from 75!)\n• Zero cases of 'I forgot to pay'\n• Customers started paying via transfer to avoid cheque deposit\n• Bad debt reduced by 80%"
      }
    ],
    dos: ["Make PDC mandatory for everyone", "Collect at delivery time, not later", "Verify all details immediately", "Store securely with proper records", "Deposit on due date if no payment"],
    donts: ["'I'll take cheque later, you're trusted'", "Accept cheque without verification", "Take personal cheque for company dues", "Not clicking photo/keeping record", "Delay depositing bounced cheque"],
    quote: "A post-dated cheque in hand is worth ten promises on phone! Make PDC mandatory, make your life easier!"
  },
  8: {
    subtitle: "Focus on Alpha customers only",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "The famous Pareto Principle (80:20 Rule) applies to customers too:\n\n20% of customers (Alpha) give you 80% of revenue with ON-TIME payments\n80% of customers (Delta/Gamma) give you 20% of revenue with DELAYED payments\n\nFocus on ALPHA customers. Fire DELTA customers!"
      },
      {
        title: "🏆 Customer Classification",
        type: "tip",
        content: "💎 ALPHA CUSTOMERS (20%):\n• Pay on time or BEFORE time\n• Large order value\n• Never ask for extra credit\n• Respect your business\n• Profitable for you\n• These are GEMS!\n\n💀 DELTA/GAMMA (80%):\n• Always delay payment\n• Small orders, big headache\n• Constantly ask for extra time\n• Never satisfied\n• Not profitable\n• These are TROUBLE!"
      },
      {
        title: "🎬 Real Business Analysis",
        type: "scenario",
        content: "A trader had 100 customers. He analyzed them:\n\nTop 20 Customers (Alpha):\n• Total sales: ₹80 lakhs (80% of ₹1 crore)\n• Average payment time: 25 days\n• Bad debt: ZERO\n• Follow-up calls needed: 1-2\n\nBottom 80 Customers (Delta/Gamma):\n• Total sales: ₹20 lakhs (20% of ₹1 crore)\n• Average payment time: 75 days\n• Bad debt: ₹2 lakhs!\n• Follow-up calls needed: 15-20!"
      },
      {
        title: "⚡ THE SHOCKING TRUTH",
        type: "highlight",
        content: "80% of your TIME, ENERGY, and STRESS comes from DELTA/GAMMA customers who give you only 20% revenue!\n\nMeanwhile, your ALPHA customers (who deserve your attention) are ignored because you're busy chasing bad customers!"
      },
      {
        title: "📊 How To Implement 80:20 Rule",
        type: "steps",
        items: [
          "Analyze Your Customers: List all customers with sales and payment data",
          "Identify Alpha (Top 20%): Highest sales + Best payment record",
          "Identify Delta (Bottom 20%): Low sales + Worst payment record",
          "Gamma (Middle 60%): Average customers - can be converted to Alpha",
          "Action Plan: Give MAXIMUM attention to Alpha, Try to convert Gamma to Alpha, STOP selling to Delta customers!"
        ]
      },
      {
        title: "📖 The Transformation Story",
        type: "story",
        content: "A manufacturer was struggling with cash flow despite ₹2 crore annual sales.\n\nAnalysis showed:\n• 150 customers total\n• Top 30 (Alpha): ₹1.6 crore sales, payments on time\n• Bottom 40 (Delta): ₹15 lakhs sales, payment after 90+ days\n• Middle 80 (Gamma): ₹25 lakhs sales, payment after 45-60 days\n\nBold Decision:\n• Stopped supplying to bottom 40 Delta customers\n• Focused 80% energy on top 30 Alpha customers\n• Spent 20% energy converting Gamma to Alpha\n\nResult after 1 year:\n• Sales remained almost same (₹1.9 crore)\n• Profit DOUBLED!\n• Cash flow became positive\n• Stress reduced by 70%\n• Bad debts became almost zero"
      }
    ],
    quote: "Stop wasting time on customers who don't value you. Focus on customers who pay on time, respect you, and make you profitable. Fire the rest!"
  },
  9: {
    subtitle: "Verify customer before giving credit",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Before giving credit to ANY customer, do a thorough background check. Just like banks verify before giving loans, you must verify before giving credit!\n\nRemember: Prevention is better than cure!"
      },
      {
        title: "⚠️ SHOCKING FACT",
        type: "highlight",
        content: "90% of payment problems happen because we DON'T verify customers before giving credit. We just trust their words!"
      },
      {
        title: "🔍 What to Verify - 8 Point Checklist",
        type: "steps",
        items: [
          "GST Verification: Check if GST number is valid on government portal",
          "Business Address: Visit physically or verify on Google Maps",
          "Years in Business: New business = Higher risk",
          "References: Ask for 2-3 supplier references and actually CALL them",
          "Bank Details: Verify bank account, check if it's active",
          "Payment History: Ask other suppliers about their payment behavior",
          "Market Reputation: Ask in your business community",
          "Credit Limit: Don't give unlimited credit to anyone"
        ]
      },
      {
        title: "🎬 Real Horror Story",
        type: "scenario",
        content: "A textile supplier got an order for ₹15 lakhs from a 'big showroom'. Customer showed fancy office, talked confidently.\n\nSupplier didn't verify - just supplied goods on 60-day credit.\n\nReality check after 60 days:\n• Office was RENTED for 3 months only\n• GST was real but business was just 6 months old\n• Customer had taken goods from 8 other suppliers similarly\n• Total fraud: ₹80 lakhs from 8 suppliers\n• Customer disappeared after 3 months!\n\nAll this could be prevented with simple background check!"
      },
      {
        title: "🏆 Coach's Background Check Formula",
        type: "tip",
        content: "For NEW customers:\n• First 3 orders: ADVANCE payment only\n• After 3 successful orders: 50% advance + 50% credit\n• After 10 successful orders: Full credit (with limit)\n• Keep reviewing every 6 months"
      },
      {
        title: "📖 Success Story - How Verification Saved ₹25 Lakhs",
        type: "story",
        content: "A machinery dealer got order for ₹25 lakhs from a 'factory'. Customer was ready to give 10% advance.\n\nDealer decided to verify first:\n• Called 3 references - 2 didn't answer, 1 said 'Payment is always delayed'\n• Visited factory location - It was a small rented warehouse, not a factory!\n• Checked online - Found complaints about this customer on social media\n\nDecision: REJECTED the order!\n\nLater found: This customer had cheated 5 suppliers in last 2 years for ₹1+ crore!\n\nOne simple background check saved ₹25 lakhs!"
      }
    ],
    dos: ["Check GST on official portal", "Visit business location", "Call references and verify", "Start with small credit limit", "Monitor payment behavior", "Review credit limit quarterly"],
    donts: ["Trust customer blindly", "Skip verification for 'big' orders", "Not calling references", "Giving unlimited credit", "Not visiting location", "Ignoring red flags"],
    quote: "Spend 2 hours on background check to save 2 years of payment recovery struggle! Verify first, supply later!"
  },
  10: {
    subtitle: "Keep legal proof of all transactions",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "In business, if it's not documented, it didn't happen! Always keep proper written records of every transaction, agreement, and communication.\n\nDocumentation is your WEAPON when payment recovery becomes difficult."
      },
      {
        title: "⚡ THE DOCUMENTATION LAW",
        type: "highlight",
        content: "NO DOCUMENTS = NO LEGAL PROOF = NO RECOVERY!"
      },
      {
        title: "📄 Essential Documents - Must Have",
        type: "steps",
        items: [
          "Purchase Order (PO): Signed order from customer with quantity, rate, delivery terms",
          "Tax Invoice: GST invoice with all legal details, signed by you",
          "Delivery Challan: Proof that goods were delivered, with customer's signature/stamp",
          "Credit Agreement: Written agreement mentioning credit period, interest on delay",
          "Post Dated Cheques: For payment security",
          "Email/WhatsApp Records: All communications saved",
          "Payment Reminders: Records of when you asked for payment",
          "Ledger Copy: Account statement showing outstanding amount"
        ]
      },
      {
        title: "🎬 Court Case Scenario",
        type: "scenario",
        content: "Supplier A (No documents):\n'Your honor, I supplied ₹10 lakhs goods to this customer 6 months ago. He's not paying!'\n\nJudge asks: 'Do you have purchase order?'\nSupplier: 'No, it was verbal order'\nJudge: 'Delivery challan with signature?'\nSupplier: 'No, my driver delivered, didn't take signature'\n\nResult: Case DISMISSED! ❌\n\nSupplier B (Proper documents):\nShows: PO, Invoice, Delivery challan with signature, Credit agreement, Email trail\n\nResult: Case WON in 3 months! ✅"
      },
      {
        title: "🏆 Document Management System",
        type: "tip",
        content: "Physical Storage:\n• Keep originals in fireproof safe\n• Customer-wise file folders\n• Easy to access when needed\n\nDigital Storage:\n• Scan all documents\n• Store in cloud (Google Drive/Dropbox)\n• Take photos of signed documents immediately\n• Backup weekly"
      },
      {
        title: "📖 The ₹50 Lakh Recovery Story",
        type: "story",
        content: "A supplier had ₹50 lakhs stuck with a customer who started denying he received goods!\n\nCustomer claimed: 'I never ordered these goods. You're trying to fraud me!'\n\nSupplier showed in court:\n1. Email chain where customer placed order\n2. WhatsApp messages confirming delivery date\n3. Purchase order with customer's signature and stamp\n4. Delivery challan with warehouse manager's signature\n5. Photos of goods being unloaded at customer's warehouse\n6. Tax invoice with all details\n\nResult: Customer had ZERO defense. Judge ordered immediate payment with interest!\n\nFull ₹50 lakhs + ₹8 lakhs interest recovered in 4 months!"
      }
    ],
    dos: ["Get signature on every document", "Take photos immediately", "Store both physical and digital", "Save all WhatsApp/Email chats", "Get stamp on delivery challan", "Keep records for 7+ years"],
    donts: ["Verbal agreements only", "No signature on delivery", "Lost or misplaced documents", "Deleted WhatsApp messages", "No proper filing system", "Trusting without documentation"],
    quote: "In business disputes, the one with better documents wins! Document everything, store safely, sleep peacefully!"
  },
  11: {
    subtitle: "Legal protection for small businesses",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "MSME (Micro, Small & Medium Enterprises) registration gives you POWERFUL legal rights to recover payments faster!\n\nIt's FREE to register and gives you protection under MSME Act 2006."
      },
      {
        title: "💎 BIGGEST BENEFIT",
        type: "highlight",
        content: "If buyer doesn't pay MSME supplier within 45 days, buyer has to pay COMPOUND INTEREST of 3X Bank Rate (approx 24-27% per year)!\n\nThis is LEGAL and ENFORCEABLE!"
      },
      {
        title: "🏆 Top 5 MSME Benefits",
        type: "steps",
        items: [
          "45-Day Payment Rule: Buyers MUST pay within 45 days of acceptance of goods/services",
          "Penal Interest: After 45 days, 24-27% interest automatically applies",
          "Fast Track Legal Process: MSME Facilitation Council resolves disputes in 90 days",
          "Priority in Courts: MSME cases get faster hearing",
          "Credibility: Customers take MSME suppliers more seriously"
        ]
      },
      {
        title: "🎬 Real Power of MSME",
        type: "scenario",
        content: "Without MSME Registration:\nCustomer owes ₹10 lakhs. Payment delayed by 6 months. You can only recover ₹10 lakhs after years of struggle.\n\nWith MSME Registration:\nCustomer owes ₹10 lakhs. Payment delayed by 6 months (180 days).\n\nYour legal claim:\n• Principal: ₹10,00,000\n• Interest @ 24% for 180 days: ₹1,18,356\n• Total recoverable: ₹11,18,356\n\nCustomer will think TWICE before delaying MSME supplier's payment!"
      },
      {
        title: "📝 How to Use MSME Power",
        type: "steps",
        items: [
          "Get Registered: Visit udyamregistration.gov.in - FREE registration in 10 minutes",
          "Mention on Invoice: Print your MSME/Udyam number on all invoices",
          "Payment Terms: Clearly mention 'Payment due in 45 days as per MSME Act'",
          "Send Reminder: On day 30, remind about 45-day rule",
          "After 45 Days: Send notice mentioning interest will apply",
          "File Complaint: If no payment, file complaint with MSME Facilitation Council"
        ]
      },
      {
        title: "📖 The ₹15 Lakh + Interest Recovery",
        type: "story",
        content: "A small parts manufacturer (MSME registered) supplied ₹15 lakhs worth components to a large automobile company.\n\nBig company's game: They had policy of paying small suppliers after 120 days!\n\nMSME supplier's action:\n1. On day 46, sent legal notice mentioning MSME Act\n2. Calculated interest @ 24% from day 46\n3. Filed complaint with MSME Facilitation Council\n4. Council summoned big company\n\nResult:\n• Big company had NO defense (MSME Act is clear!)\n• Received ₹15 lakhs + ₹73,000 interest in 60 days\n• Big company now pays this supplier within 30 days!"
      }
    ],
    dos: ["Register immediately (it's FREE!)", "Print Udyam number on invoices", "Mention 45-day rule in agreements", "Send reminder on day 30", "Calculate interest after 45 days", "Use MSME Council for disputes"],
    donts: ["Not registering (missing huge benefit!)", "Not mentioning MSME number", "Accepting 90-120 day terms", "Not claiming interest", "Forgetting to mention Act in invoice", "Going to regular court instead of Council"],
    quote: "MSME registration is like having a government lawyer working for you FREE! Register today, protect yourself forever!"
  },
  12: {
    subtitle: "Take credit, sell cash for cash flow",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Learn from the Pani Puri wallah - the smartest businessman in India!\n\nHis model: Buy ingredients on CREDIT from wholesaler, sell Pani Puri for CASH to customers!\n\nThis is the PERFECT cash flow model for any business!"
      },
      {
        title: "🎬 The Pani Puri Model Explained",
        type: "scenario",
        content: "Day 1: Pani Puri seller buys ₹5,000 worth ingredients on 30-day credit\nDay 1-30: Sells Pani Puri for CASH, earns ₹15,000\nDay 30: Pays ₹5,000 to wholesaler from collected cash\n\nResult: ₹10,000 profit + Zero cash flow problem!\n\nMeanwhile, most businesses do OPPOSITE:\nBuy on CASH, sell on CREDIT → Result: Always cash crunch!"
      },
      {
        title: "💡 THE PERFECT FORMULA",
        type: "highlight",
        content: "BUY ON CREDIT → SELL ON CASH\nOR\nBUY ON 60 DAYS → SELL ON 30 DAYS\n\nYour selling credit should be LESS than buying credit!"
      },
      {
        title: "🏆 How to Implement Pani Puri Model",
        type: "steps",
        items: [
          "Negotiate with Suppliers: Ask for 60-90 days credit from your suppliers",
          "Reduce Customer Credit: Give only 30-45 days credit to customers",
          "Create Gap: This 30-45 day gap is your safety net!",
          "Use Float: You get customer payment BEFORE paying supplier",
          "Reinvest: Use this cash to grow business"
        ]
      },
      {
        title: "📊 Cash Flow Comparison",
        type: "tip",
        content: "❌ WRONG MODEL (Most Businesses):\nExample: ₹10 lakh business\n• Buy from supplier: CASH payment\n• Sell to customer: 90-day credit\n• Result: ₹10 lakhs blocked for 90 days!\n• Need to take LOAN to survive\n• Always cash crunch!\n\n✅ RIGHT MODEL (Pani Puri Style):\nExample: Same ₹10 lakh business\n• Buy from supplier: 60-day credit\n• Sell to customer: 30-day credit\n• Result: Get ₹10 lakhs in 30 days\n• Pay supplier after 60 days\n• 30-day cash float available!\n• NO loan needed!"
      },
      {
        title: "📖 The Transformation Story",
        type: "story",
        content: "A garment wholesaler was always in cash crunch despite good sales of ₹50 lakhs/month.\n\nOld Model:\n• Bought fabric: Cash payment to mills\n• Sold garments: 60-day credit to retailers\n• Result: Always taking loans @ 18% interest\n\nChanged to Pani Puri Model:\n• Negotiated 45-day credit from mills\n• Reduced customer credit to 30 days\n• Strict: No extension!\n\nResult after 6 months:\n• Cleared all loans\n• 15-day positive cash float\n• Saved ₹6 lakhs/year on interest\n• Could give cash discount to get better prices\n• Business grew 40% without taking ANY loan!"
      }
    ],
    quote: "Be smart like the Pani Puri seller - Buy on credit, sell on cash. This is the secret to never running out of cash in business!"
  },
  13: {
    subtitle: "Know minimum sales needed to survive",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Break-even point is the minimum sales you need to cover all expenses. Below this, you're in LOSS. Above this, you make PROFIT!\n\nMost business owners don't know their break-even - they just keep working blindly!"
      },
      {
        title: "⚡ SHOCKING TRUTH",
        type: "highlight",
        content: "80% of Indian businesses are running at LOSS without knowing it! They think they're making profit but actually expenses are more than profit!"
      },
      {
        title: "🧮 Break Even Formula (Simplified)",
        type: "scenario",
        content: "Step 1: Calculate Total Fixed Expenses\n• Shop/Office rent\n• Staff salaries\n• Electricity, phone bills\n• Loan EMI\n• Insurance, licenses\n• Your own salary\nExample: ₹3,00,000 per month\n\nStep 2: Calculate Gross Profit Margin\nIf you sell product for ₹100 and cost is ₹70, GP = ₹30 (30%)\n\nStep 3: Break Even Sales\nFormula: Fixed Expenses ÷ GP%\nExample: ₹3,00,000 ÷ 30% = ₹10,00,000\nMeaning: You need minimum ₹10 lakhs sales to survive!"
      },
      {
        title: "🏆 Why Break-Even is Critical",
        type: "steps",
        items: [
          "Know Your Target: You know exact sales needed to survive",
          "Plan Better: Can calculate how much profit at different sales levels",
          "Control Expenses: If sales drop, you know which expenses to cut",
          "Price Better: Helps you decide minimum price you can offer",
          "Avoid Shock: No surprises at month-end!"
        ]
      },
      {
        title: "📖 The Wake-Up Call Story",
        type: "story",
        content: "A retail shop owner thought he was making good profit. Sales were ₹8 lakhs/month.\n\nWhen calculated break-even:\n• Fixed expenses: ₹2,50,000/month\n• Gross profit margin: 25%\n• Break-even: ₹2,50,000 ÷ 25% = ₹10,00,000\n\nSHOCK! His sales were ₹8 lakhs but break-even was ₹10 lakhs!\nMeaning: He was making ₹50,000 LOSS every month without knowing!\n\nActions taken:\n1. Reduced staff from 5 to 3 (₹40,000 saved)\n2. Moved to slightly smaller shop (₹30,000 saved)\n3. New break-even: ₹7,20,000\n4. Now making ₹20,000+ profit/month!"
      }
    ],
    dos: ["Calculate break-even monthly", "Track actual vs break-even sales", "Reduce fixed expenses regularly", "Increase GP% through better pricing", "Set sales target above break-even", "Review every quarter"],
    donts: ["Never calculating break-even", "Ignoring fixed expenses", "Not tracking monthly", "High fixed costs", "Working without target", "Assuming you're profitable"],
    quote: "If you don't know your break-even, you don't know your business! Calculate it today, save your business tomorrow!"
  },
  14: {
    subtitle: "Cash is king in business survival",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "You can show PROFIT on paper and still go BANKRUPT!\n\nBecause PROFIT ≠ CASH\n\nMany profitable businesses shut down due to cash flow problems!"
      },
      {
        title: "⚠️ THE HARSH REALITY",
        type: "highlight",
        content: "PROFIT is OPINION\nCASH is FACT!\n\nYou can't pay salaries with 'profit on paper' - you need ACTUAL CASH!"
      },
      {
        title: "🎬 The Profitable But Bankrupt Business",
        type: "scenario",
        content: "Scenario: Company X shows ₹50 lakh profit on books\n\nReality check:\n• Sales: ₹2 crore (₹1.5 crore stuck in credit!)\n• Stock: ₹80 lakhs (not converted to cash)\n• Fixed assets: ₹40 lakhs (machinery, furniture)\n• Bank balance: ₹2 lakhs only!\n\nProblem arises:\n• Salary day: ₹5 lakhs needed\n• Supplier payment: ₹8 lakhs due\n• Rent + bills: ₹3 lakhs\n• Total needed: ₹16 lakhs\n• Available: ₹2 lakhs!\n\nResult: Profitable on paper but can't pay bills! 😱"
      },
      {
        title: "🏆 Cash Flow vs Profit - Understanding",
        type: "tip",
        content: "📊 PROFIT (On Paper):\n• Includes credit sales (money not received)\n• Doesn't show when money comes\n• Looks at annual/monthly numbers\n• Can be manipulated\n• Example: Sold ₹10L goods on credit = Shows ₹10L revenue (but ₹0 in bank!)\n\n💰 CASH FLOW (Reality):\n• Only counts money actually received\n• Shows exactly when money comes/goes\n• Daily/weekly tracking\n• Cannot be faked\n• Example: Bank balance ₹50,000 = That's what you actually have!"
      },
      {
        title: "📊 Cash Flow Management - 5 Rules",
        type: "steps",
        items: [
          "Track Daily: Know your bank balance every single day",
          "Reduce Credit Period: Collect payments faster (30 days better than 90)",
          "Control Stock: Don't block cash in inventory",
          "Negotiate with Suppliers: Take longer credit from suppliers",
          "Maintain Cash Reserve: Keep 3 months expenses as emergency fund"
        ]
      },
      {
        title: "📖 The ₹3 Crore Sales But Bankrupt Story",
        type: "story",
        content: "A manufacturing company had impressive numbers:\n• Annual sales: ₹3 crore\n• Profit on paper: ₹45 lakhs (15%)\n• Growing 30% year-on-year\n\nBut reality:\n• ₹1.8 crore stuck in credit (customers paying after 90-120 days)\n• ₹60 lakhs blocked in stock\n• Owner's family expenses: ₹3 lakhs/month (taken from business)\n• Bank balance: Always less than ₹5 lakhs!\n\nThe crisis:\nOne month, 3 customers delayed payment together. ₹40 lakhs didn't come on time.\nCompany couldn't pay salaries. Couldn't buy raw material. Had to shut down temporarily!\n\nAll this while showing ₹45 lakh profit on books!\n\nLesson learned: Changed focus from profit to cash flow. Reduced credit period to 45 days. Maintained ₹15 lakh cash reserve. Business became stable!"
      }
    ],
    quote: "Profit is what you show on tax returns. Cash flow is what keeps you in business. Focus on CASH!"
  },
  15: {
    subtitle: "Warning signs of business failure",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Coach Rushneek had to shut down his business TWICE because he ignored these warning signs!\n\nLearn from his mistakes - if you see these red flags, take IMMEDIATE action!"
      },
      {
        title: "⚠️ CRITICAL WARNING",
        type: "highlight",
        content: "If you have 3 or more of these problems, your business is in DANGER ZONE! Act NOW before it's too late!"
      },
      {
        title: "🚨 The 7 Red Flags of Business Failure",
        type: "scenario",
        content: "⛔ RED FLAG #1: Taking Loans to Pay Salaries\nWarning: You're taking loans or using credit cards to pay staff salaries\nMeaning: Revenue is not enough to cover basic expenses!\n\n⛔ RED FLAG #2: Selling Stock at Loss\nWarning: Selling inventory below cost price to arrange cash\nMeaning: Severe cash flow crisis!\n\n⛔ RED FLAG #3: Unable to Pay Yourself\nWarning: Haven't taken your own salary for 2-3 months\nMeaning: Business is not viable!\n\n⛔ RED FLAG #4: Avoiding Supplier Calls\nWarning: Avoiding calls because you can't pay them\nMeaning: Cash flow is broken!\n\n⛔ RED FLAG #5: Using Personal Money for Business\nWarning: Regularly putting personal savings into business\nMeaning: Business is not self-sustainable!\n\n⛔ RED FLAG #6: Increasing Bad Debts\nWarning: More than 20% of receivables are over 90 days old\nMeaning: You're selling to wrong customers!\n\n⛔ RED FLAG #7: Fixed Cost Above 10%\nWarning: Fixed expenses are more than 10% of sales\nMeaning: Overhead is too high!"
      },
      {
        title: "🏆 Coach's Emergency Action Plan",
        type: "steps",
        items: [
          "Stop Loan-Credit Model: DON'T take loans and sell on credit. If must take loan → Sell on LESS credit (7-14 days max)",
          "Liquidate Stock: STOP production. Try to SELL OUT your stock. Convert stock to CASH!",
          "Prevent Bad Debts: Go LEGAL, send NOTICES. Don't let bad debts become write-offs!",
          "Cut Fixed Costs: Reduce rent, reduce staff, cut unnecessary expenses",
          "Focus ONLY on Collections: Stop new sales. Focus 100% on collecting old payments"
        ]
      },
      {
        title: "📖 Coach Rushneek's Personal Confession",
        type: "story",
        content: "I had shirt manufacturing business in Gandhi Nagar. I ignored ALL these red flags:\n\n✗ Was taking loans to buy raw material\n✗ Couldn't pay myself for 4 months\n✗ Had ₹25 lakhs stuck in bad debts\n✗ Fixed costs were 15% of sales\n✗ Kept hoping 'next month will be better'\n\nResult: Had to SHUT DOWN business!\n\nStarted again, ignored red flags AGAIN. Shut down SECOND time!\n\nThird time, I learned. Now helping 6,500+ business owners avoid same mistakes!\n\nDon't be like the old me. Act when you see red flags!"
      }
    ],
    dos: ["Track red flags monthly", "Act immediately when you see warning signs", "Cut costs ruthlessly", "Focus on cash collection", "Maintain 3-month expense reserve", "Get professional help early"],
    donts: ["Ignoring warning signs", "Hoping 'it will get better'", "Taking more loans to survive", "Not cutting costs fast enough", "Ego preventing tough decisions", "Waiting until it's too late"],
    quote: "Red flags are your business's way of crying for help. Ignore them and lose your business. Act on them and save your business. Choice is yours!"
  },
  16: {
    subtitle: "Quality customers over quantity",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Don't get too EXCITED about sales! Not all sales are good sales. A sale to a WRONG customer is actually a LOSS!\n\nGamma and Delta customers are CANCER for your business - they will destroy you slowly!"
      },
      {
        title: "⚡ THE SHOCKING TRUTH",
        type: "highlight",
        content: "One Alpha customer paying ₹10L on time\nis BETTER than\nTen Delta customers owing ₹10L each but never paying!"
      },
      {
        title: "🎬 The Excited Salesman Trap",
        type: "scenario",
        content: "New customer comes: 'I want to order ₹20 lakhs worth products!'\n\nYour excitement: 'WOW! ₹20 lakhs! Big customer!'\n\nYou forget to check:\n✗ Credit history\n✗ Payment capacity\n✗ Business stability\n✗ References\n\nResult 90 days later:\nCustomer doesn't pay. Avoids calls. You're stuck with ₹20 lakhs!\n\nThat 'exciting' ₹20L sale just became your biggest headache!"
      },
      {
        title: "🏆 Alpha vs Delta Customer",
        type: "tip",
        content: "💎 ALPHA CUSTOMER:\n• Pays BEFORE or ON time\n• Respects your business\n• Doesn't haggle excessively\n• Gives repeat orders\n• Values quality\n• Refers other good customers\n• Makes you PROFITABLE!\n\n💀 DELTA/GAMMA CUSTOMER:\n• ALWAYS delays payment\n• Gives excuses constantly\n• Wants maximum discount\n• Complains about everything\n• Never satisfied\n• Makes your life hell\n• Makes you BANKRUPT!"
      },
      {
        title: "📖 The ₹50 Lakh Sales = ₹15 Lakh Loss Story",
        type: "story",
        content: "A distributor was desperate for sales. Got an order from a new retailer for ₹50 lakhs.\n\nWarning signs (which he ignored):\n• Customer's shop was newly opened (just 3 months)\n• Asked for 90-day credit (distributor's policy was 30 days)\n• References were not reachable\n• Location was in disputed area\n\nBut he got EXCITED about ₹50L sales!\nDelivered goods on 90-day credit.\n\nReality after 90 days:\n• Customer sold some goods, couldn't sell rest\n• Returned damaged goods worth ₹15 lakhs (claimed they were defective)\n• Paid only ₹20 lakhs after 6 months\n• Remaining ₹15 lakhs never recovered!\n\nThat 'amazing' ₹50L sale resulted in ₹15L permanent loss + 6 months of stress!"
      },
      {
        title: "🎯 How to Avoid Slipping on Sales",
        type: "steps",
        items: [
          "Set Standards: Define what makes a customer qualified (payment history, credit score, references)",
          "Say NO: If customer doesn't meet standards, politely REFUSE",
          "Start Small: New customers start with small orders, cash payment",
          "Check References: Always call and verify references",
          "Trust Your Gut: If something feels wrong, it probably is!",
          "Quality Over Quantity: 10 Alpha customers > 100 Delta customers"
        ]
      }
    ],
    quote: "Don't slip over sales! Not every customer deserves your products. Choose customers as carefully as they choose suppliers. Quality customers = Quality business life!"
  },
  17: {
    subtitle: "Go extra mile for customer loyalty",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "The customer who is LOYAL to you will pay you on time, every time!\n\nHow to make loyal customers? GO THE EXTRA MILE! Over-deliver on your promises!"
      },
      {
        title: "👞 The Famous BATA Sandal Story",
        type: "story",
        content: "Coach Rushneek shares his personal experience:\n\n'I was wearing BATA sandals. The strap broke while I was walking.\n\nI went to BATA showroom thinking I'll have to buy new sandals.\n\nThe shopkeeper said: 'Sir, no problem. We'll replace the strap FREE. Right now!'\n\nHe didn't ask for bill. Didn't argue. Just FIXED it in 2 minutes!\n\nCost to BATA: ₹10 for the strap\nResult: I've bought 30+ sandals from BATA since then!\nValue: ₹50,000+ in sales from that ₹10 investment!\n\nThat's the power of going EXTRA MILE!'"
      },
      {
        title: "💡 THE LOYALTY FORMULA",
        type: "highlight",
        content: "Whatever is PROMISED → DO IT!\nWhatever is EXPECTED → EXCEED IT!"
      },
      {
        title: "🏆 How to Create Loyal Customers",
        type: "steps",
        items: [
          "Deliver on Time: If promised delivery on 15th, deliver on 14th!",
          "Quality Guaranteed: If product has issue, replace immediately without argument",
          "Be Available: Answer customer's calls even after sale is done",
          "Solve Problems Fast: Customer complaint? Fix it within 24 hours!",
          "Add Value: Give them tips, insights, help them grow their business",
          "Personal Touch: Remember their birthday, family details, preferences"
        ]
      },
      {
        title: "🎬 Two Suppliers - Different Approaches",
        type: "scenario",
        content: "Supplier A (Just Doing Business):\n• Delivers on promised date (sometimes 1-2 days late)\n• Quality is 'okay' - as per standard\n• Calls only when payment is due\n• Argues when customer has complaint\n• Always pushing for more orders\n\nSupplier B (Going Extra Mile):\n• Delivers 1 day BEFORE promised date\n• Quality is BETTER than promised\n• Calls weekly just to check 'everything okay?'\n• Replaces faulty product immediately, no questions asked\n• Shares market insights, helps customer grow\n• Remembers customer's birthday, sends wishes\n\nQuestion: Which supplier gets paid on time?\nObviously Supplier B! Customer feels GRATEFUL!"
      },
      {
        title: "🎯 Loyalty = Timely Payments",
        type: "tip",
        content: "When customer is loyal to you:\n✓ They pay you BEFORE paying others\n✓ They don't make excuses for delays\n✓ They give you repeat orders\n✓ They refer other customers to you\n✓ They defend you if someone talks bad about you\n✓ They become your BRAND AMBASSADORS!"
      }
    ],
    dos: ["Over-deliver on promises", "Respond to complaints in 24 hours", "Give small surprises (extra quantity, discount)", "Be available when they need you", "Help them succeed in their business", "Treat them like family"],
    donts: ["Promising something, not delivering", "Arguing on complaints", "Calling only for orders/payments", "Being difficult to reach", "Not caring about their success", "Treating them as just 'transactions'"],
    quote: "The customer who pays on time is the one you've served EXTRA well. Make them love you, and they'll pay you happily!"
  },
  18: {
    subtitle: "How to find absconding customers",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Customer took your goods worth lakhs and now he's DISAPPEARED? Phone switched off? Office closed? Don't panic!\n\nThere are PROVEN methods to find absconding customers!"
      },
      {
        title: "🎬 The Typical Disappearing Act",
        type: "scenario",
        content: "Phase 1 (Day 1-30): Customer is friendly, taking your calls\nPhase 2 (Day 31-60): Starting to avoid calls, making excuses\nPhase 3 (Day 61+): Phone switched off, office locked, disappeared!\n\nNow what? Here's your action plan:"
      },
      {
        title: "🕵️ 5 Ways to Find Absconding Customers",
        type: "tip",
        content: "🔍 Method 1: CIBIL Report (₹500-₹1000)\nMost effective and legal way!\n• Shows customer's CURRENT ADDRESS from bank records\n• Shows all loans and credit cards\n• Shows if they have active bank accounts\n• Success Rate: 80%+\n\n🕵️ Method 2: Detective Agency\nWhen everything else fails!\n• Professional detectives can find anyone\n• Cost: ₹5,000-₹20,000 depending on case\n• Usually find within 7-15 days\n• Worth it if amount is ₹5 lakhs+\n\n🏡 Method 3: Visit Native Place\nOld but gold method!\n• Every person has a native place/village\n• Go there, talk to neighbors/relatives\n• People in village usually know where person is\n\n📧 Method 4: Legal Notice to ALL Addresses\nCreate legal pressure!\n• Send notice to business address\n• Send to registered office address\n• Send to residential address (if known)\n• Send to native place address\n• One of these will reach them!\n\n📱 Method 5: Social Media + Online Search\nDigital age solution!\n• Search on Facebook, LinkedIn, Instagram\n• Check WhatsApp last seen, profile photo changes\n• Look for tagged photos (shows current location)\n• Check their family members' social media"
      },
      {
        title: "📖 The ₹5 Lakh CIBIL Success Story",
        type: "story",
        content: "A textile trader had ₹5 lakhs stuck with a customer who completely disappeared.\n\nWhat happened:\n• Customer's shop was closed\n• Phone switched off permanently\n• Residential address was old - family had moved\n• No one knew where he went\n\nAction taken:\n• Got customer's CIBIL report for ₹500\n• Found that he took a car loan 2 months ago\n• Car loan application showed his NEW address in different city!\n• Also found his new mobile number from loan records\n\nResult:\n• Visited the new address\n• Customer was shocked - thought he escaped!\n• Recovered FULL ₹5 lakhs within 15 days!\n\n₹500 CIBIL report saved ₹5 lakhs!"
      },
      {
        title: "⚠️ PREVENTION IS BETTER",
        type: "highlight",
        content: "To avoid 'gayab' situations, collect these BEFORE giving credit:\n✓ 2-3 personal references with phone numbers\n✓ Permanent address (native place)\n✓ Alternate phone numbers (family members)\n✓ Copy of Aadhaar and PAN card\n✓ GST registration certificate\n✓ Post-dated cheques"
      }
    ],
    quote: "An absconding customer is NOT lost money. It's money that needs FINDING. Use these tools and RECOVER IT!"
  },
  19: {
    subtitle: "Blue Ocean vs Red Ocean business",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Most businesses fail NOT because of bad execution, but because of NO PLANNING!\n\nThey enter a market without understanding DEMAND vs SUPPLY dynamics!"
      },
      {
        title: "💡 THE PLANNING LAW",
        type: "highlight",
        content: "If you FAIL TO PLAN,\nyou automatically PLAN TO FAIL!"
      },
      {
        title: "🌊 Blue Ocean vs Red Ocean Business",
        type: "tip",
        content: "🌊 BLUE OCEAN (Good for You!):\n• Low Competition: Few players in market\n• High Demand: Many customers need it\n• Less Supply: Not many suppliers\n• Better Margins: Can charge premium\n• Easy Sales: Customers come to you\n• Example: EV charging stations (2023-24), Solar panels (2020)\n\n🩸 RED OCEAN (Avoid This!):\n• High Competition: Thousands of players\n• Low Margins: Constant price war\n• Saturated Market: Everyone selling same thing\n• Hard to Survive: Big players dominate\n• Race to Bottom: Who sells cheapest wins\n• Example: Mobile covers, T-shirts, General store, Stationery"
      },
      {
        title: "🎬 Two Entrepreneurs - Different Planning",
        type: "scenario",
        content: "Entrepreneur A (No Planning):\n'My friend is making money selling mobile accessories. Let me also start the same business!'\n• Didn't check: 50 other shops already selling in same area\n• Didn't realize: Market is saturated\n• Result: Price war, low margins, shut down in 8 months\n\nEntrepreneur B (Proper Planning):\n'Let me research which business has demand but less supply...'\n• Found: EV bike charging points are needed\n• Researched: Only 2 providers in city of 50 lakh population\n• Analyzed: 20,000 EV bikes already running, growing 50% yearly\n• Result: Started business, no competition, premium pricing, successful!"
      },
      {
        title: "🏆 The Planning Checklist",
        type: "steps",
        items: [
          "Demand: How many people/businesses NEED this product/service?",
          "Supply: How many are ALREADY providing it?",
          "Gap Analysis: Is demand MORE than supply? (Blue Ocean) Or supply MORE than demand? (Red Ocean)",
          "Competition: Can you beat existing players? Do you have unique advantage?",
          "Profit Margins: Can you make at least 20-25% profit?",
          "Sustainability: Will this business work for next 5-10 years?"
        ]
      },
      {
        title: "📖 The Blue Ocean Discovery",
        type: "story",
        content: "In 2019, a businessman was confused about what business to start.\n\nHis research process:\n• Made list of 10 possible businesses\n• For each, counted demand vs supply in his city\n• Found that 'Corporate gift customization' had high demand but only 3 suppliers!\n• Companies needed customized gifts but had limited options\n\nStarted business with proper planning:\n• Year 1: ₹50 lakhs revenue, 30% profit margin\n• Year 2: ₹1.2 crore revenue, still good margins\n• Year 3: ₹2 crore revenue\n• Competition increased but he was already established\n\nHe chose BLUE OCEAN, avoided RED OCEAN!"
      }
    ],
    quote: "Don't jump into RED OCEAN where everyone is bleeding. Find your BLUE OCEAN where you can swim freely and profitably!"
  },
  20: {
    subtitle: "The 7-day before rule",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Don't wait for due date to ask for payment!\n\nStart your follow-up 7 DAYS BEFORE the due date. Give customer time to ARRANGE money!"
      },
      {
        title: "⏰ THE 7-DAY RULE",
        type: "highlight",
        content: "Ask 7 days BEFORE = Get paid ON TIME\nAsk ON due date = Get paid LATE\nAsk AFTER due date = Get paid NEVER!"
      },
      {
        title: "🎬 Why 7 Days Before?",
        type: "scenario",
        content: "Customer's Perspective:\n'If supplier reminds me 7 days before, I have time to:'\n✓ Arrange funds from different sources\n✓ Collect money from my customers\n✓ Talk to bank for short-term loan\n✓ Plan my cash flow\n✓ Keep supplier payment in priority list\n\nBut if you ask ON due date:\n'Sorry, I already planned my cash flow. All money is allocated. Can you wait 15 more days?'"
      },
      {
        title: "📅 The 7-Day Payment Collection Timeline",
        type: "steps",
        items: [
          "Day -7 (First Reminder): Polite WhatsApp/SMS - 'Dear Sir, your payment of ₹5,00,000 is due on 31st March. Kindly arrange. Thanks!'",
          "Day -5 (Second Reminder): Email with statement of account - 'Sir, as discussed, please find attached statement. Payment due in 5 days. Please confirm.'",
          "Day -3 (Phone Call): Personal follow-up call - 'Hello Sir, just checking - payment of ₹5 lakhs is due in 3 days. Is everything arranged?'",
          "Day -1 (Final Reminder): Confirmation message - 'Sir, payment is due tomorrow (31st March). Kindly arrange. Our bank details: XYZ...'",
          "Due Date (Day 0): Confirm receipt or reason - 'Sir, today is payment due date. Kindly confirm payment status.'"
        ]
      },
      {
        title: "🏆 Smart Communication Templates",
        type: "tip",
        content: "Professional Reminder (Day -7):\n'Dear [Name],\nTrust you are doing well!\nThis is to remind that payment of ₹[Amount] for Invoice No. [XXX] dated [Date] is due on [Due Date].\nRequest you to kindly arrange the same.\nThanks & Regards,\n[Your Name]'\n\nFriendly Reminder (Day -3):\n'Hi [Name],\nHope everything is good!\nSmall reminder - our payment of ₹[Amount] is due on [Date] (3 days from now).\nPlease let me know if you need any documents or have any queries.\nThanks!'"
      },
      {
        title: "📖 The On-Time Payment Transformation",
        type: "story",
        content: "A distributor had 40 customers. Average payment delay: 15-20 days after due date.\n\nOld approach:\n• Wait for due date\n• Then start calling\n• Customer says 'didn't know payment was due'\n• Another 15-20 days to get payment\n\nNew approach (7-day rule):\n• Started sending reminder 7 days before due date\n• Follow-up on day -5, day -3, day -1\n• Customer NEVER forgot about payment\n• Had time to arrange funds\n\nResults after 3 months:\n• 28 out of 40 customers started paying ON TIME!\n• Remaining 12 paid within 5 days max\n• Average delay reduced from 20 days to 2 days!\n• Cash flow became predictable\n• No more running after payments"
      }
    ],
    dos: ["Start reminders 7 days before", "Use multiple channels (WhatsApp, Email, Call)", "Be polite but persistent", "Provide all details (invoice, bank details)", "Keep record of all reminders", "Thank them when they pay on time"],
    donts: ["Waiting for due date to remind", "Sending only one reminder", "Being too pushy or rude", "Not following up systematically", "Assuming customer remembers", "Getting emotional or angry"],
    quote: "The businessperson who asks for payment BEFORE time, gets paid ON time. It's simple psychology - keep yourself on TOP OF MIND!"
  },
  21: {
    subtitle: "Criminal offense = Your leverage",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "When customer's cheque bounces, don't feel sad - feel HAPPY!\n\nWhy? Because cheque bounce is a CRIMINAL OFFENSE under Section 138 of Negotiable Instruments Act!\n\nCustomer can go to JAIL for this! This is your STRONGEST leverage!"
      },
      {
        title: "⚖️ THE POWER OF SECTION 138",
        type: "highlight",
        content: "Cheque bounce is NOT a civil case - it's a CRIMINAL CASE!\n\nPunishment:\n✓ UP TO 2 YEARS JAIL\n✓ OR Fine up to DOUBLE the cheque amount\n✓ OR BOTH (Jail + Fine)\n\nThat's why it's GOOD NEWS for you!"
      },
      {
        title: "⏰ The 30-Day Action Timeline",
        type: "scenario",
        content: "Day 1: Cheque Bounces\n• Bank sends you 'Cheque Return Memo'\n• Reason: 'Insufficient Funds' or 'Account Closed' etc.\n• GET THIS MEMO - it's your evidence!\n\nWithin 30 Days: Send Legal Notice\n• MUST send notice within 30 days of cheque bounce\n• Send via REGISTERED POST with acknowledgment\n• Demand payment within 15 days\n• This is MANDATORY legal requirement!\n\nWait 15 Days\n• Give customer 15 days to pay after receiving notice\n• Most customers PAY at this stage (to avoid jail!)\n• If they pay, case closed\n\nAfter 15 Days: File Case\n• If no payment, file case in Magistrate Court\n• Within 1 month after 15-day notice period\n• Court will issue summons to customer"
      },
      {
        title: "🏆 4 Essential Documents You Need",
        type: "steps",
        items: [
          "Original Bounced Cheque: The actual cheque that bounced (don't lose it!)",
          "Cheque Return Memo: From bank showing reason for bounce",
          "Legal Notice Copy: Copy of notice sent + postal receipt/acknowledgment",
          "Invoice/Bill: Proof of transaction - why cheque was given"
        ]
      },
      {
        title: "📖 The ₹8 Lakh Recovery Story",
        type: "story",
        content: "A manufacturer supplied ₹8 lakhs worth machinery. Customer gave post-dated cheque.\n\nCheque bounced!\n• Customer stopped taking calls\n• Thought he can escape\n\nSupplier's action:\n1. Got cheque return memo from bank (Day 1)\n2. Sent legal notice via registered post (Day 5)\n3. Notice mentioned Section 138 + Jail threat (Day 7)\n4. Customer got notice (Day 10)\n5. Customer's lawyer advised: 'PAY immediately or face jail!'\n\nResult:\n• Customer PANICKED!\n• Called supplier begging for settlement\n• Paid FULL ₹8 lakhs within 12 days!\n• Case didn't even need to go to court\n\nThe THREAT of jail was enough!"
      },
      {
        title: "💡 Why Customers Fear Cheque Bounce Cases",
        type: "tip",
        content: "• Criminal Record: Gets permanently recorded\n• Arrest Warrant: Police can arrest without warrant!\n• Jail Time: Can be sent to jail for up to 2 years\n• Business Reputation: Criminal case damages reputation\n• Bank Relationship: Banks blacklist for loans\n• Future Credit: No one gives credit to cheque bouncer\n\nThat's why 80% customers pay after receiving legal notice!"
      }
    ],
    dos: ["Always take post-dated cheques", "Get cheque return memo immediately", "Send legal notice within 30 days", "Keep all documents safely", "File case if no payment in 15 days", "Follow legal procedure strictly"],
    donts: ["Miss 30-day notice period (case dismissed!)", "Send notice without proof", "Lose original cheque", "Not keeping cheque return memo", "Accepting excuses after bounce", "Delaying legal action"],
    quote: "A bounced cheque is your WEAPON, not your loss. Use Section 138 NI Act and watch customers pay within 30 days - they fear JAIL more than you!"
  },
  22: {
    subtitle: "Absconding vs No Money - Different strategies!",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "There are TWO types of non-paying customers:\n\n1. Customer is ABSCONDING (running away, hiding)\n2. Customer has NO MONEY (really facing crisis)\n\nThese two require COMPLETELY DIFFERENT recovery strategies!"
      },
      {
        title: "🎯 FIRST: Identify Which Type?",
        type: "highlight",
        content: "TYPE 1: ABSCONDING\n✗ Not taking calls\n✗ Shop/office closed\n✗ Changed address\n✗ Running away deliberately\n\nTYPE 2: NO MONEY\n✓ Takes your calls\n✓ Business still running\n✓ Genuinely in crisis\n✓ Wants to pay but can't"
      },
      {
        title: "🔴 LAW #1: For ABSCONDING Customers",
        type: "scenario",
        content: "USE LEGAL PRESSURE IMMEDIATELY!\n\nWhy? Because:\n• This customer is CHEATING you deliberately\n• Has NO intention to pay\n• Will keep running if you don't take action\n• Time is your ENEMY - act fast!\n\nYour Action Plan:\nWeek 1: Try to locate through all possible means\n• Call all known numbers\n• Visit office/home address\n• Contact through common network\n• Check social media\n\nWeek 2: Send legal notice\n• To last known address\n• Via registered post\n• Mention legal consequences\n\nWeek 3-4: Take legal action\n• File police complaint (if cheating/fraud)\n• File court case\n• Get court summons issued\n\n⚠️ CRITICAL: For absconding customers, being 'nice' or 'patient' is STUPID. They are CHEATERS. Hit them with full legal force!"
      },
      {
        title: "🟢 LAW #2: For NO MONEY Customers",
        type: "tip",
        content: "USE PATIENCE + RESTRUCTURING!\n\nWhy? Because:\n• Customer is GENUINELY in trouble\n• Wants to pay but temporarily stuck\n• Legal action will only DESTROY his business\n• If his business dies, you'll NEVER get paid!\n\nYour Action Plan:\n1. Understand the situation\n• Why is he facing crisis?\n• Is it temporary or permanent?\n• Does his business have recovery potential?\n\n2. Negotiate payment plan\n• Break payment into smaller EMIs\n• Accept part payment now\n• Give realistic timeline\n• Get post-dated cheques for balance\n\n3. Take security\n• Ask for additional security/collateral\n• Personal guarantee from family\n• Mortgage of property/assets\n\n4. Monitor regularly\n• Weekly/fortnightly follow-up\n• Track his business recovery\n• Collect small payments regularly\n\n💡 WISDOM: For genuine customers in crisis, be their PARTNER in recovery. Help them revive. When they bounce back, you'll get 100% + future business!"
      },
      {
        title: "📖 Two Customers, Two Strategies",
        type: "story",
        content: "Case 1: The Absconding Customer (₹12 Lakhs)\nRetailer took ₹12 lakhs worth stock. Gave post-dated cheques. All bounced!\n• Shop suddenly closed\n• Phone switched off\n• Address changed\nAction taken: Immediate legal notice → Police complaint → Court case\nResult: Customer arrested after 4 months. Settled ₹10 lakhs to avoid jail!\n\nCase 2: The No-Money Customer (₹8 Lakhs)\nDistributor owed ₹8 lakhs. Business genuinely collapsed due to COVID.\n• Still operating from home\n• Taking calls, explaining situation\n• Sold personal car to pay partial amount (₹2 lakhs)\nAction taken: Restructured payment - ₹50k per month for 12 months + post-dated cheques\nResult: Customer paid ALL ₹8 lakhs in 14 months! Even started giving new orders!\n\nThe Lesson: Same problem (non-payment), but DIFFERENT causes need DIFFERENT solutions!"
      }
    ],
    dos: ["Identify: Absconding or No Money?", "Absconding = Legal pressure immediately", "No Money = Patience + restructuring", "Take proper security in both cases", "Document everything", "Keep emotions aside, think practically"],
    donts: ["Same strategy for all customers", "Being too soft on absconding customers", "Being too harsh on genuine crisis customers", "Delaying action on fraudsters", "Giving up on recovery", "Acting emotionally without analysis"],
    quote: "Be a LION with absconding customers (hunt them legally). Be a FRIEND with genuine crisis customers (help them recover). Know the difference!"
  },
  23: {
    subtitle: "Learn from failures - avoid these deadly habits!",
    sections: [
      {
        title: "📖 The Core Principle",
        content: "Success leaves clues. But FAILURE leaves even bigger clues!\n\nCoach Rushneek personally did business for 19 years. Made CRORES. Lost CRORES.\n\nThese are the 9 DEADLY HABITS that killed businesses (including his own!). AVOID them at ALL costs!"
      },
      {
        title: "⚠️ HABIT #1: Not Maintaining Books of Accounts",
        type: "scenario",
        content: "The Problem:\n• No proper accounting\n• Don't know who owes how much\n• Can't track profit/loss\n• Cash flow is a mystery\n\nCoach's Personal Story:\n'In my textile business, I thought I was making profit because I had cash in hand. But when I actually did accounting after 2 years - I was running in LOSS of ₹18 lakhs! Cash was coming from selling goods below cost. I was basically EATING my capital thinking it's profit!'\n\n✅ Solution: Maintain daily books. Know your numbers DAILY, not yearly!"
      },
      {
        title: "⚠️ HABIT #2: Spending on Liabilities, Not Assets",
        type: "scenario",
        content: "The Problem:\n• Buying expensive car (EMI ₹50k/month)\n• Taking big office on rent\n• Spending on lifestyle to 'show off'\n• Business profit going into personal luxuries\n\nCoach's Personal Story:\n'I bought Audi Q7 on loan when my business was doing ₹50 lakhs/year. EMI was ₹65,000. I thought I'm successful because I drive Audi. But that car ate my cash flow! The same ₹65k could have been invested in inventory or marketing. Today I know: Rich people buy ASSETS, poor people buy LIABILITIES!'\n\n✅ Solution: Reinvest profit in business. Buy car AFTER business is stable!"
      },
      {
        title: "⚠️ HABITS #3-5: Credit, Follow-up & Money Mixing",
        type: "tip",
        content: "HABIT #3: Giving Credit Without Checking\n• Giving goods on credit to anyone who asks\n• Not checking customer's background\n• Trusting on 'word of mouth'\nCoach lost ₹8 lakhs to a 'friend of XYZ' - no papers, no recovery!\n✅ Solution: Follow credit policy religiously!\n\nHABIT #4: Not Following Up on Payments\n• Feeling shy to ask for money\n• Waiting for customer to pay on their own\n• Being 'too nice'\nCoach had ₹42 lakhs stuck - recovered ₹28 lakhs only after systematic follow-up!\n✅ Solution: Follow up 7 days before due date!\n\nHABIT #5: Mixing Personal & Business Money\n• Using business cash for personal expenses\n• No separate business bank account\nCoach spent ₹12 lakhs on family wedding from business cash - business collapsed!\n✅ Solution: ALWAYS maintain separate accounts!"
      },
      {
        title: "⚠️ HABITS #6-9: Expansion, Legal, Learning & Emotions",
        type: "tip",
        content: "HABIT #6: Over-Expansion Without Cash\n• Opening multiple branches too fast\n• Expanding before stabilizing current business\nCoach opened 3 branches, spent ₹25 lakhs, closed all within a year!\n✅ Solution: Expand ONLY when you have 2x cash buffer!\n\nHABIT #7: Not Taking Legal Action on Time\n• Delaying legal notice thinking 'customer will pay'\n• Missing legal timelines\nCoach lost ₹6 lakhs because he waited 90 days - notice period expired!\n✅ Solution: Take legal action IMMEDIATELY!\n\nHABIT #8: Not Learning & Upgrading Skills\n• Thinking 'I know everything'\n• Not reading books or attending seminars\n• Repeating same mistakes\n✅ Solution: Invest in KNOWLEDGE!\n\nHABIT #9: Being Too Emotional in Business\n• Giving credit because 'he's my relative'\n• Not taking action because 'he's my friend'\nCoach's brother-in-law took ₹10 lakhs - never paid, never took legal action!\n✅ Solution: Business is business. Keep emotions separate!"
      },
      {
        title: "🎯 Coach Rushneek's Final Message",
        type: "story",
        content: "I'm sharing these 9 habits not from books - but from my PERSONAL FAILURES!\n\nI made ALL these mistakes. Lost crores. Faced depression. Bankruptcy. Divorce.\n\nBut I LEARNED. I changed. I rebuilt.\n\nToday, I teach payment recovery not because I'm PERFECT - but because I FAILED and learned from it!\n\nYour job: DON'T repeat my mistakes. Learn from them. Save yourself YEARS of pain!\n\nRemember: Smart people learn from their mistakes. WISE people learn from others' mistakes!\n\nBe wise. Implement these 23 principles. Build a business that THRIVES, not just survives!\n\n- Coach Rushneek Singh"
      }
    ],
    dos: ["Maintain proper accounts daily", "Reinvest profit in assets, not liabilities", "Follow strict credit policy", "Follow up systematically on payments", "Separate personal & business money", "Expand only with cash buffer", "Take legal action on time", "Keep learning & upgrading", "Be professional, not emotional"],
    donts: ["No accounting/tracking", "Spending on lifestyle to show off", "Giving credit without checking", "Being too shy to ask for money", "Mixing personal & business cash", "Over-expanding too fast", "Delaying legal action", "Not learning from failures", "Making emotional decisions"],
    quote: "The difference between SUCCESS and FAILURE is not intelligence or luck - it's HABITS! Change your habits, change your destiny! 🚀"
  }
};

function PrinciplesSection({ principles, onPrincipleClick, selectedPrinciple, onCloseModal }) {
  const getPrincipleNumber = (principle) => {
    return principles.findIndex(p => p.title === principle.title) + 1;
  };

  const renderModalContent = (principle) => {
    const num = getPrincipleNumber(principle);
    const details = principleDetails[num];
    
    if (!details) {
      return (
        <div className="p-6">
          <p className="text-gray-700 text-lg leading-relaxed mb-6">{principle.desc}</p>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
            <p className="text-amber-800 font-medium">
              💡 Pro Tip: Apply this principle consistently to see real results in your payment recovery journey.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6 space-y-6">
        {details.sections.map((section, idx) => (
          <div key={idx}>
            {section.type === 'scenario' ? (
              <div className="bg-gradient-to-r from-sky-50 to-blue-50 p-5 rounded-xl border-l-4 border-blue-500">
                <h4 className="text-blue-900 font-bold text-lg mb-3">{section.title}</h4>
                <p className="text-slate-700 whitespace-pre-line">{section.content}</p>
              </div>
            ) : section.type === 'highlight' ? (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-5 rounded-xl border-l-4 border-amber-500">
                <h4 className="text-amber-900 font-bold text-lg mb-3">{section.title}</h4>
                <p className="text-slate-800 font-semibold text-center whitespace-pre-line">{section.content}</p>
              </div>
            ) : section.type === 'tip' ? (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border-l-4 border-green-500">
                <h4 className="text-green-900 font-bold text-lg mb-3">{section.title}</h4>
                <p className="text-slate-700 whitespace-pre-line">{section.content}</p>
              </div>
            ) : section.type === 'story' ? (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-5 rounded-xl border-l-4 border-purple-500">
                <h4 className="text-purple-900 font-bold text-lg mb-3">{section.title}</h4>
                <p className="text-slate-700 whitespace-pre-line italic">{section.content}</p>
              </div>
            ) : section.type === 'steps' ? (
              <div className="bg-white p-5 rounded-xl border border-gray-200">
                <h4 className="text-blue-900 font-bold text-lg mb-3">{section.title}</h4>
                <ol className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700">
                      <FaCheck className="text-green-500 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <div>
                <h4 className="text-blue-900 font-bold text-lg mb-3">{section.title}</h4>
                <p className="text-slate-700 whitespace-pre-line">{section.content}</p>
              </div>
            )}
          </div>
        ))}

        {(details.dos || details.donts) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.dos && (
              <div className="bg-green-50 p-5 rounded-xl border border-green-200">
                <h4 className="text-green-800 font-bold mb-3">✅ DO THIS</h4>
                <ul className="space-y-2">
                  {details.dos.map((item, i) => (
                    <li key={i} className="text-green-700 text-sm">• {item}</li>
                  ))}
                </ul>
              </div>
            )}
            {details.donts && (
              <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                <h4 className="text-red-800 font-bold mb-3">❌ DON'T DO THIS</h4>
                <ul className="space-y-2">
                  {details.donts.map((item, i) => (
                    <li key={i} className="text-red-700 text-sm">• {item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {details.quote && (
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-5 rounded-xl text-center">
            <p className="text-white text-lg italic">"{details.quote}"</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-3xl p-6 sm:p-8 md:p-10 mb-10 shadow-xl border border-slate-100">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaGraduationCap className="text-lg" />
            <span>Complete Learning Module</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            All 23 Payment Recovery Principles
          </h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            Click on any principle card to explore detailed content with real-world examples, legal frameworks, and actionable strategies
          </p>
          
          {/* Feature Tags */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {[
              { icon: <FaBook className="text-blue-500" />, text: "Detailed Examples", bg: "bg-blue-50", border: "border-blue-200" },
              { icon: <FaBalanceScale className="text-purple-500" />, text: "Legal Frameworks", bg: "bg-purple-50", border: "border-purple-200" },
              { icon: <HiLightBulb className="text-amber-500" />, text: "Coach Tips", bg: "bg-amber-50", border: "border-amber-200" },
              { icon: <FaCheckCircle className="text-emerald-500" />, text: "Do's & Don'ts", bg: "bg-emerald-50", border: "border-emerald-200" },
            ].map((item, i) => (
              <div key={i} className={`flex items-center gap-2 ${item.bg} ${item.border} border px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-slate-700 text-xs sm:text-sm font-medium`}>
                {item.icon}
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Principles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 md:gap-6">
          {principles.map((principle, index) => (
            <div 
              key={index}
              onClick={() => onPrincipleClick(principle)}
              className="group relative bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-md hover:shadow-2xl cursor-pointer border border-slate-100 hover:border-blue-300 transition-all duration-500 hover:-translate-y-3 overflow-hidden"
            >
              {/* Animated Background Gradient on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Top Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-t-full" />
              
              {/* Card Content */}
              <div className="relative z-10">
                {/* Number Badge & Icon Row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold text-sm sm:text-base shadow-lg group-hover:shadow-blue-300/50 group-hover:scale-110 transition-all duration-300">
                    {principle.num}
                  </div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 group-hover:from-blue-50 group-hover:to-indigo-50 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                    {principle.icon && <principle.icon className="text-2xl sm:text-3xl text-blue-600 group-hover:text-indigo-600 transition-colors" />}
                  </div>
                </div>
                
                {/* Title */}
                <h4 className="text-slate-800 group-hover:text-blue-900 font-bold text-sm sm:text-base mb-2 min-h-[44px] sm:min-h-[50px] leading-tight transition-colors duration-300">
                  {principle.title}
                </h4>
                
                {/* Description */}
                <p className="text-slate-500 text-xs sm:text-sm line-clamp-2 mb-4">
                  {principle.desc}
                </p>
                
                {/* Bottom Action */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 group-hover:border-blue-100 transition-colors">
                  <span className="text-xs text-slate-400 group-hover:text-blue-500 font-medium transition-colors">
                    Click to learn more
                  </span>
                  <div className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-blue-500 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <FaArrowRight className="text-slate-400 group-hover:text-white text-xs transition-colors" />
                  </div>
                </div>
              </div>
              
              {/* Corner Decoration */}
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-xl" />
            </div>
          ))}
        </div>
        
        {/* Bottom Stats */}
        <div className="mt-10 sm:mt-12 pt-8 border-t border-slate-200">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
            {[
              { value: "23", label: "Principles", icon: <FaBook className="text-blue-500" /> },
              { value: "50+", label: "Real Stories", icon: <BsBookmarkStarFill className="text-amber-500" /> },
              { value: "100+", label: "Action Tips", icon: <BsLightningChargeFill className="text-purple-500" /> },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl shadow-sm border border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-lg">
                  {stat.icon}
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-slate-800">{stat.value}</div>
                  <div className="text-xs text-slate-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedPrinciple && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          onClick={onCloseModal}
        >
          <div className="h-full w-full flex items-center justify-center p-2 sm:p-4 md:p-6">
            <div 
              className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sticky Header */}
              <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl shadow-lg">
                {/* Close Button */}
                <button 
                  onClick={onCloseModal}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center text-lg sm:text-xl font-bold transition-all duration-300 hover:rotate-90 hover:scale-110 border border-white/20"
                >
                  <FaTimes />
                </button>
                
                {/* Header Content */}
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 pr-10 sm:pr-12">
                  {/* Principle Number Badge */}
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-black text-lg sm:text-2xl shadow-lg transform rotate-3 hover:rotate-0 transition-transform">
                      {selectedPrinciple.num}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center">
                      {selectedPrinciple.icon && <selectedPrinciple.icon className="text-white text-lg sm:text-2xl" />}
                    </div>
                  </div>
                  
                  {/* Title & Subtitle */}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {selectedPrinciple.title}
                    </h2>
                    <p className="text-blue-200 text-xs sm:text-sm mt-1 line-clamp-2">
                      {principleDetails[getPrincipleNumber(selectedPrinciple)]?.subtitle || selectedPrinciple.desc}
                    </p>
                  </div>
                </div>
                
                {/* Progress Indicator */}
                <div className="flex items-center gap-2 mt-3 sm:mt-4">
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                      style={{ width: `${(getPrincipleNumber(selectedPrinciple) / 23) * 100}%` }}
                    />
                  </div>
                  <span className="text-white/70 text-xs font-medium">
                    {getPrincipleNumber(selectedPrinciple)}/23
                  </span>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto overscroll-contain">
                <div className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
                  {principleDetails[getPrincipleNumber(selectedPrinciple)] ? (
                    <>
                      {/* Sections */}
                      {principleDetails[getPrincipleNumber(selectedPrinciple)].sections.map((section, idx) => (
                        <div key={idx} className="animate-fadeIn" style={{ animationDelay: `${idx * 50}ms` }}>
                          {section.type === 'scenario' ? (
                            <div className="bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                                  <span className="text-white text-sm">🎬</span>
                                </div>
                                <h4 className="text-blue-900 font-bold text-base sm:text-lg">{section.title}</h4>
                              </div>
                              <p className="text-slate-700 whitespace-pre-line text-sm sm:text-base leading-relaxed">{section.content}</p>
                            </div>
                          ) : section.type === 'highlight' ? (
                            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-amber-200 shadow-sm relative overflow-hidden">
                              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                                  <span className="text-white text-sm">⚡</span>
                                </div>
                                <h4 className="text-amber-900 font-bold text-base sm:text-lg">{section.title}</h4>
                              </div>
                              <p className="text-slate-800 font-semibold text-center whitespace-pre-line text-sm sm:text-base md:text-lg relative z-10">{section.content}</p>
                            </div>
                          ) : section.type === 'tip' ? (
                            <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-green-200 shadow-sm">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                                  <span className="text-white text-sm">🏆</span>
                                </div>
                                <h4 className="text-emerald-900 font-bold text-base sm:text-lg">{section.title}</h4>
                              </div>
                              <p className="text-slate-700 whitespace-pre-line text-sm sm:text-base leading-relaxed">{section.content}</p>
                            </div>
                          ) : section.type === 'story' ? (
                            <div className="bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-purple-200 shadow-sm relative">
                              <div className="absolute top-4 right-4 text-4xl opacity-10">📖</div>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                                  <span className="text-white text-sm">📖</span>
                                </div>
                                <h4 className="text-purple-900 font-bold text-base sm:text-lg">{section.title}</h4>
                              </div>
                              <p className="text-slate-700 whitespace-pre-line italic text-sm sm:text-base leading-relaxed">{section.content}</p>
                            </div>
                          ) : section.type === 'steps' ? (
                            <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm">
                              <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                                  <span className="text-white text-sm">✓</span>
                                </div>
                                <h4 className="text-blue-900 font-bold text-base sm:text-lg">{section.title}</h4>
                              </div>
                              <ol className="space-y-3">
                                {section.items.map((item, i) => (
                                  <li key={i} className="flex items-start gap-3 text-slate-700 bg-slate-50 p-3 rounded-xl hover:bg-slate-100 transition-colors">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                      {i + 1}
                                    </span>
                                    <span className="text-sm sm:text-base">{item}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          ) : (
                            <div className="bg-slate-50 p-4 sm:p-6 rounded-xl sm:rounded-2xl">
                              <h4 className="text-blue-900 font-bold text-base sm:text-lg mb-3">{section.title}</h4>
                              <p className="text-slate-700 whitespace-pre-line text-sm sm:text-base leading-relaxed">{section.content}</p>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Do's and Don'ts */}
                      {(principleDetails[getPrincipleNumber(selectedPrinciple)].dos || principleDetails[getPrincipleNumber(selectedPrinciple)].donts) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {principleDetails[getPrincipleNumber(selectedPrinciple)].dos && (
                            <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-green-200 shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-lg">✅</div>
                                <h4 className="text-green-800 font-bold text-sm sm:text-base">DO THIS</h4>
                              </div>
                              <ul className="space-y-2">
                                {principleDetails[getPrincipleNumber(selectedPrinciple)].dos.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2 text-green-700 text-xs sm:text-sm bg-white/60 p-2 rounded-lg">
                                    <FaCheck className="text-green-500 mt-0.5 flex-shrink-0 text-xs" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {principleDetails[getPrincipleNumber(selectedPrinciple)].donts && (
                            <div className="bg-gradient-to-br from-red-50 to-rose-100 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-red-200 shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-lg">❌</div>
                                <h4 className="text-red-800 font-bold text-sm sm:text-base">DON'T DO THIS</h4>
                              </div>
                              <ul className="space-y-2">
                                {principleDetails[getPrincipleNumber(selectedPrinciple)].donts.map((item, i) => (
                                  <li key={i} className="flex items-start gap-2 text-red-700 text-xs sm:text-sm bg-white/60 p-2 rounded-lg">
                                    <FaTimes className="text-red-500 mt-0.5 flex-shrink-0 text-xs" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Quote */}
                      {principleDetails[getPrincipleNumber(selectedPrinciple)].quote && (
                        <div className="relative bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 p-5 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
                          <div className="absolute top-2 left-4 text-6xl text-white/10 font-serif">"</div>
                          <div className="absolute bottom-2 right-4 text-6xl text-white/10 font-serif rotate-180">"</div>
                          <p className="text-white text-sm sm:text-base md:text-lg text-center italic relative z-10 leading-relaxed">
                            "{principleDetails[getPrincipleNumber(selectedPrinciple)].quote}"
                          </p>
                          <div className="flex justify-center mt-4">
                            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                              <span className="text-amber-400">⭐</span>
                              <span className="text-white/80 text-xs sm:text-sm font-medium">Coach Rushneek Singh</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-4 sm:p-6">
                      <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">{selectedPrinciple.desc}</p>
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-xl">
                        <p className="text-amber-800 font-medium text-sm sm:text-base">
                          💡 Pro Tip: Apply this principle consistently to see real results in your payment recovery journey.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Action Bar */}
                <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-4 px-4 sm:px-6 md:px-8">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-100 p-3 sm:p-4 rounded-xl sm:rounded-2xl">
                    <div className="text-slate-600 text-xs sm:text-sm text-center sm:text-left">
                      <span className="font-semibold text-blue-900">Principle {getPrincipleNumber(selectedPrinciple)}</span> of 23 Payment Recovery Principles
                    </div>
                    <button 
                      onClick={onCloseModal}
                      className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-sm sm:text-base"
                    >
                      Close & Continue Learning
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PrinciplesSection;
